// Written by scripts/generate.ts from schema/. Change the schema, then run `npm run generate`.
import type { KEY_CODES } from '../values.ts';

/** The codes of the packets a client sends. */
export const CLIENT_PACKETS = {
  LOGIN: 0,
  BACKUP: 1,
  HORIZON: 2,
  ACK: 5,
  PONG: 6,
  KEY: 10,
  COMMAND: 11,
  SCOREDETAILED: 12,
  CHAT: 20,
  WHISPER: 21,
  SAY: 22,
  TEAMCHAT: 23,
  VOTEMUTE: 24,
  SYNC_START: 200,
  SYNC_AUTH: 201,
  SYNC_INIT: 202,
  SYNC_UPDATE: 204,
  SYNC_ACK: 205,
  LOCALPING: 255,
} as const;

/** Any packet code a client sends. */
export type ClientPacketCode = (typeof CLIENT_PACKETS)[keyof typeof CLIENT_PACKETS];

// =============================================================================
// Login and connection
// =============================================================================

/** The first packet on a new connection: the protocol version, the player's name, session, view size and flag. */
export interface ClientLoginMessage {
  c: typeof CLIENT_PACKETS.LOGIN;
  /** Protocol version, 5 */
  protocol: number;
  /** Desired player name */
  name: string;
  /** Session token (a JSON string, or "none") */
  session: string;
  /** Half the view width, in map units */
  horizonX: number;
  /** Half the view height, in map units */
  horizonY: number;
  /** Country flag's ISO code, a key of FLAGS_ISO_TO_CODE; any other code asks for the flag of the player's address */
  flag: string;
}

/** The first packet on a backup connection, which ties it to the player through the LOGIN reply's token. */
export interface ClientBackupMessage {
  c: typeof CLIENT_PACKETS.BACKUP;
  /** The token from the LOGIN reply */
  token: string;
}

/** The player's view size, which bounds what the server sends them. */
export interface ClientHorizonMessage {
  c: typeof CLIENT_PACKETS.HORIZON;
  /** Half the view width, in map units */
  horizonX: number;
  /** Half the view height, in map units */
  horizonY: number;
}

/** The keepalive; an empty packet. */
export interface ClientAckMessage {
  c: typeof CLIENT_PACKETS.ACK;
}

/** The answer to a PING. */
export interface ClientPongMessage {
  c: typeof CLIENT_PACKETS.PONG;
  /** The PING's random number */
  num: number;
}

// =============================================================================
// Play
// =============================================================================

/** A control key pressed or released. */
export interface ClientKeyMessage {
  c: typeof CLIENT_PACKETS.KEY;
  /** Sequence number, which orders the key packets */
  seq: number;
  /** Which of the six control keys */
  key: KEY_CODES;
  /** Pressed (true) or released (false) */
  state: boolean;
}

/** A command for the server, such as spectate, respawn or flag. */
export interface ClientCommandMessage {
  c: typeof CLIENT_PACKETS.COMMAND;
  /** Command name */
  com: string;
  /** Command's argument */
  data: string;
}

/** Asks for the detailed scoreboard; an empty packet. */
export interface ClientScoreDetailedMessage {
  c: typeof CLIENT_PACKETS.SCOREDETAILED;
}

// =============================================================================
// Chat
// =============================================================================

/** A chat line for everyone. */
export interface ClientChatMessage {
  c: typeof CLIENT_PACKETS.CHAT;
  /** Message text */
  text: string;
}

/** A private message to one player. */
export interface ClientWhisperMessage {
  c: typeof CLIENT_PACKETS.WHISPER;
  /** Recipient's player ID */
  id: number;
  /** Message text */
  text: string;
}

/** A speech bubble over the player's airplane. */
export interface ClientSayMessage {
  c: typeof CLIENT_PACKETS.SAY;
  /** Message text */
  text: string;
}

/** A chat line for the player's team. */
export interface ClientTeamChatMessage {
  c: typeof CLIENT_PACKETS.TEAMCHAT;
  /** Message text */
  text: string;
}

/** A vote to mute a player. */
export interface ClientVoteMuteMessage {
  c: typeof CLIENT_PACKETS.VOTEMUTE;
  /** The player's ID */
  id: number;
}

// =============================================================================
// Sync service, server to server
// =============================================================================

/** Starts a sync connection; an empty packet. */
export interface ClientSyncStartMessage {
  c: typeof CLIENT_PACKETS.SYNC_START;
}

/** Authenticates a sync connection by answering the server's challenge. */
export interface ClientSyncAuthMessage {
  c: typeof CLIENT_PACKETS.SYNC_AUTH;
  /** Token holding the signed challenge */
  response: string;
}

/** Initialises a sync connection. */
export interface ClientSyncInitMessage {
  c: typeof CLIENT_PACKETS.SYNC_INIT;
  /** Next sequence number the server is to use */
  sequence: number;
  /** Current time at the sync service, in milliseconds since the Unix epoch */
  timestamp: number;
  /** Server identity (region-room) */
  serverId: string;
  /** The server's public WebSocket endpoint (wss://host/path) */
  wsEndpoint: string;
}

/** An object's data, from the sync service to the game server. */
export interface ClientSyncUpdateMessage {
  c: typeof CLIENT_PACKETS.SYNC_UPDATE;
  /** Replace the whole object */
  complete: boolean;
  /** Object type */
  type: string;
  /** Object ID */
  id: string;
  /** Object data as JSON */
  data: string;
}

/** Acknowledges a game server's SYNC_UPDATE. */
export interface ClientSyncAckMessage {
  c: typeof CLIENT_PACKETS.SYNC_ACK;
  /** The update's sequence number */
  sequence: number;
  /** Zero when the update applied, else an error code */
  result: number;
}

// =============================================================================
// Unused
// =============================================================================

/** Currently not used. */
export interface ClientLocalPingMessage {
  c: typeof CLIENT_PACKETS.LOCALPING;
  /** Currently not used */
  auth: number;
}

/** Every packet a client sends, told apart by c. */
export type ClientMessage =
  | ClientLoginMessage
  | ClientBackupMessage
  | ClientHorizonMessage
  | ClientAckMessage
  | ClientPongMessage
  | ClientKeyMessage
  | ClientCommandMessage
  | ClientScoreDetailedMessage
  | ClientChatMessage
  | ClientWhisperMessage
  | ClientSayMessage
  | ClientTeamChatMessage
  | ClientVoteMuteMessage
  | ClientSyncStartMessage
  | ClientSyncAuthMessage
  | ClientSyncInitMessage
  | ClientSyncUpdateMessage
  | ClientSyncAckMessage
  | ClientLocalPingMessage;
